/**
 * Public site settings context (maintenance, registration, fees). Provider lives in
 * PublicSiteSettingsProvider.tsx.
 */

import {
  createContext,
  use,
} from 'react';

import type { SiteSettings } from '@lambda/shared/types';

import { resolveDirectorEmail } from '@utils/directorEmail';

export interface PublicSiteSettingsValue {
  settings: SiteSettings | null;
  /** True until the first GET /site-settings completes. */
  loading: boolean;
  /** True when settings are not yet known (no cache and fetch in flight). */
  registrationPending: boolean;
  /** True when settings indicate new registrations are allowed (uses cache immediately when present). */
  registrationsOpen: boolean;
  showRegistrationClosedNotice: boolean;
  registrationClosedNoticeText: string;
  /** Director email from App Settings, with built-in fallback. */
  directorEmail: string;
  refetch: () => Promise<void>;
}

const defaultValue: PublicSiteSettingsValue = {
  settings: null,
  loading: true,
  registrationPending: true,
  registrationsOpen: false,
  showRegistrationClosedNotice: false,
  registrationClosedNoticeText: '',
  directorEmail: resolveDirectorEmail(null),
  refetch: async () => {},
};

export const PublicSiteSettingsContext = createContext<PublicSiteSettingsValue>(defaultValue);

export function usePublicSiteSettings(): PublicSiteSettingsValue {
  return use(PublicSiteSettingsContext);
}
